import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { CheckToken } from "../login/AccessToken";
import { C, getAuthHeaders } from "../ipd-billing/constants";
import { formatDateTime } from "../lab-technician/labOrdersShared";

const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;

/** Same template ids the IPD timeline / nurse Doctor Instructions use. */
const CLINICAL_NOTES_TEMPLATE_ID = 8;
const NOTE_TEMPLATE_ID = 2;
const DOCTOR_NOTE_TEMPLATE_IDS = [NOTE_TEMPLATE_ID, CLINICAL_NOTES_TEMPLATE_ID];

const CLINICAL_TEXT_FIELDS = [
  ["condition", "Condition"],
  ["diagnosis", "Diagnosis"],
  ["history_of_present_illness", "History of Present Illness"],
  ["co_morbidities", "Co-morbidities"],
  ["past_medical_history", "Past Medical History"],
  ["personal_social_history", "Personal / Social History"],
  ["notes", "Note"],
  ["reason", "Reason"],
  ["category", "Category"],
  ["goals", "Goals"],
];

const CLINICAL_TABLE_FIELDS = [
  [
    "current_medications",
    "Current Medications",
    (r) =>
      [r.medicine_name, r.frequency, r.quantity].filter(Boolean).join(" · "),
  ],
  [
    "past_medications",
    "Past Medications",
    (r) =>
      [
        r.medicine_name,
        r.frequency,
        r.quantity,
        r.number_of_days && `${r.number_of_days}d`,
      ]
        .filter(Boolean)
        .join(" · "),
  ],
  [
    "past_surgical_history",
    "Past Surgical History",
    (r) => [r.surgery, r.date, r.remarks].filter(Boolean).join(" · "),
  ],
  [
    "family_history",
    "Family History",
    (r) => [r.relation, r.ailment, r.since_date].filter(Boolean).join(" · "),
  ],
];

function asText(value) {
  if (Array.isArray(value)) {
    return value
      .filter((x) => x != null && typeof x !== "object")
      .join(", ");
  }
  if (value == null || typeof value === "object") return "";
  return String(value);
}

function noteTimestamp(note) {
  const raw =
    note?.created_at ||
    note?.data?.__timeline_created_at ||
    note?.data?.__timeline_sort_ts;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : 0;
}

function extractCarePlanList(payload) {
  const tryArr = (v) => (Array.isArray(v) ? v : null);
  const d = payload?.data;
  return (
    tryArr(d?.care_plans) ||
    tryArr(d?.care_plan_entries) ||
    tryArr(d?.entries) ||
    tryArr(d?.results) ||
    tryArr(d?.care_plan_list) ||
    tryArr(d?.items) ||
    tryArr(d) ||
    tryArr(payload?.care_plans) ||
    tryArr(payload?.results) ||
    tryArr(payload) ||
    []
  );
}

function templateLabel(note) {
  const id = Number(note?.template_id);
  if (id === CLINICAL_NOTES_TEMPLATE_ID) return "Clinical Notes";
  if (id === NOTE_TEMPLATE_ID) return "Note";
  return note?.name || note?.structure_name || "Clinical entry";
}

function NoteEntry({ note }) {
  const d = note?.data || {};
  const author =
    note?.created_by?.name ||
    note?.created_by_name ||
    note?.doctor_name ||
    "";
  const whenRaw =
    note?.created_at ||
    d.__timeline_created_at ||
    null;
  const when = whenRaw ? formatDateTime(whenRaw) : null;
  const texts = CLINICAL_TEXT_FIELDS.map(([k, label]) => [
    k,
    label,
    asText(d[k]),
  ]).filter(([, , value]) => value.trim() !== "");
  const tables = CLINICAL_TABLE_FIELDS.filter(
    ([k]) => Array.isArray(d[k]) && d[k].length > 0,
  );

  return (
    <Box
      sx={{
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        p: 1.75,
        background: "#fff",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.text }}>
          {templateLabel(note)}
        </Typography>
        <Typography sx={{ fontSize: 12, color: C.textSub }}>
          {[author && `Dr. ${author}`, when && when !== "-" ? when : null]
            .filter(Boolean)
            .join(" · ") || "—"}
        </Typography>
      </Stack>
      {texts.length === 0 && tables.length === 0 ? (
        <Typography sx={{ mt: 1, fontSize: 13, color: C.textSub }}>
          No clinical details recorded in this entry.
        </Typography>
      ) : (
        <Stack spacing={1.25} sx={{ mt: 1.25 }}>
          {texts.map(([k, label, value]) => (
            <Box key={k}>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: C.textSub }}>
                {label}
              </Typography>
              <Typography
                sx={{ fontSize: 13, color: C.text, whiteSpace: "pre-wrap" }}
              >
                {value}
              </Typography>
            </Box>
          ))}
          {tables.map(([k, label, fmt]) => (
            <Box key={k}>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: C.textSub }}>
                {label}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {d[k].map((row, idx) => (
                  <li key={idx}>
                    <Typography component="span" sx={{ fontSize: 13 }}>
                      {fmt(row)}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/**
 * Read-only IPD clinical notes for diagnostic review — same GET care_plans/
 * source as the IPD patient timeline.
 */
export default function IpdPatientConditionDialog({
  open,
  onClose,
  admissionId,
  patientName,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    const resolved = String(admissionId || "").trim();
    if (!resolved) {
      setNotes([]);
      setError("Missing admission id for this IPD patient.");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setNotes([]);

    CheckToken();
    axios
      .get(`${BASE}care_plans/`, {
        params: { admission_id: resolved },
        headers: getAuthHeaders(),
        skipGlobalLoader: true,
      })
      .then((res) => {
        if (cancelled) return;
        const body = res?.data;
        if (body?.status && body.status !== "Success") {
          setError(body?.message || "Could not load clinical notes.");
          setNotes([]);
          return;
        }
        const list = extractCarePlanList(body);
        const filtered = list
          .filter((r) =>
            DOCTOR_NOTE_TEMPLATE_IDS.includes(Number(r?.template_id)),
          )
          .sort((a, b) => noteTimestamp(b) - noteTimestamp(a));
        setNotes(filtered);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load IPD clinical notes:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load clinical notes.",
        );
        setNotes([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, admissionId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // Above the report PDF overlay (z-index ~2001).
      sx={{ zIndex: 2200 }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        Patient condition
        {patientName ? (
          <Typography
            component="span"
            sx={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSub, mt: 0.5 }}
          >
            {patientName} · IPD clinical notes
          </Typography>
        ) : null}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, maxHeight: "70vh" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ fontSize: 13 }}>
            {error}
          </Typography>
        ) : notes.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: C.textSub }}>
            No clinical notes found for this admission yet.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {notes.map((note, idx) => (
              <NoteEntry
                key={note?.id || note?.care_plan_data_id || `note-${idx}`}
                note={note}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={onClose} variant="contained" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
